import React, { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { 
  Card, Button, Form, Input, Checkbox, Typography, Space, 
  Tag, Divider, Tooltip, Slider, App, Select, Upload, Segmented, Image, Modal, List 
} from 'antd'
import { 
  VideoCameraOutlined, 
  PictureOutlined, 
  PlayCircleOutlined, 
  DownloadOutlined,
  HistoryOutlined,
  SoundOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  RocketOutlined,
  FireOutlined,
  CrownOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined,
  DeleteOutlined,
  ReloadOutlined,
  BulbOutlined,
  CloseOutlined,
  PlusOutlined,
  SettingOutlined,
  UserOutlined,
  ClearOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { apiClient } from '@/services/api'
import { useAuthStore } from '@/store/authStore'
import { VideoModel, VideoGenerationType, VideoOrientation } from '@/types'
import { on as onEvent, off as offEvent } from '@/services/events'
import { GlobalStyles, COMMON_STYLES } from '@/styles/globalStyles'
import '@/styles/global.css'
import { textService } from '@/services/textService'

const { Title, Paragraph, Text } = Typography
const { Dragger } = Upload
const { Option } = Select
const { TextArea } = Input

type RefImageItem = { file: File; url: string; previewUrl: string }

const VideoGenerationPage: React.FC = () => {
  const { message, modal } = App.useApp()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  // 🔥 按钮文本状态：'generating' - 生成中, 'waiting' - 预估等待
  const [buttonTextState, setButtonTextState] = useState<'generating' | 'waiting' | null>(null)
  
  const [selectedModel, setSelectedModel] = useState<string>('')
  const [generationType, setGenerationType] = useState<VideoGenerationType>('TEXT_TO_VIDEO')
  const [orientation, setOrientation] = useState<VideoOrientation>('PORTRAIT')
  const [hasAudio, setHasAudio] = useState(false)
  const [duration, setDuration] = useState(15)
  const [currentGeneration, setCurrentGeneration] = useState<any>(null)
  const [refImageFile, setRefImageFile] = useState<File | null>(null)
  const [refImageUrl, setRefImageUrl] = useState<string | null>(null)
  const [refImagePreviewUrl, setRefImagePreviewUrl] = useState<string | null>(null)  // 用于本地预览的URL
  // 🔥 Veo3 多参考图：最多 3 张，统一由前端决定 firstFrameUrl / lastFrameUrl / urls 模式
  const [refImages, setRefImages] = useState<RefImageItem[]>([])
  const [uploadingImage, setUploadingImage] = useState(false)
  const [inlineError, setInlineError] = useState<string | null>(null)
  const [mobileTab, setMobileTab] = useState<'create' | 'result'>('create')
  const [isMobile, setIsMobile] = useState(false)
  const navigate = useNavigate()

  // 检测是否为移动端
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])
  const [inspirations, setInspirations] = useState<any[]>([])
  const [loadingInspirations, setLoadingInspirations] = useState(false)
  const [videoModels, setVideoModels] = useState<any[]>([])
  const [loadingModels, setLoadingModels] = useState(false)
  const [videoProviders, setVideoProviders] = useState<any[]>([])
  const [loadingProviders, setLoadingProviders] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState<string>('')
  const [promptValue, setPromptValue] = useState<string>('')
  const promptInputRef = useRef<any>(null)
  const [polishing, setPolishing] = useState(false)
  const [characters, setCharacters] = useState<Array<{ url: string; timestamps: string }>>([])
  const [characterModalVisible, setCharacterModalVisible] = useState(false)
  const [availableCharacters, setAvailableCharacters] = useState<Array<{
    id: string
    name: string
    videoUrl: string
    timestamps: string
    sourceType: string
    isGlobal?: boolean
    useCost?: number
  }>>([])
  const [selectedCharacterIds, setSelectedCharacterIds] = useState<string[]>([])
  const [newCharacterForm] = Form.useForm()
  const [creatingCharacter, setCreatingCharacter] = useState(false)

  // 防重复点击保护
  const lastClickTimeRef = useRef(0)
  const MIN_CLICK_INTERVAL = 5000 // 5秒最小间隔，防止重复提交

  // 加载视频服务商配置
  const loadProviders = async () => {
    try {
      setLoadingProviders(true)
      // 🔥 修复：使用公开API而不是管理员API
      const response = await apiClient.get('/video/models')
      
      if (response.success && response.data) {
        const { models } = response.data
        
        // 直接将API返回的模型转换为前端格式，并过滤启用的模型
        // 规范化 priority 为数字
        const normalized = models.map((model: any) => ({
          ...model,
          priority: Number(model.priority ?? 999)
        }))

        const allModels = normalized
          .filter((model: any) => model.enabled) // 🔥 修复：只显示启用的模型
          .map((model: any) => ({
            id: model.id,
            name: model.name,
            provider: model.provider,
            providerName: model.providerName,
            description: `${model.providerName} - ${model.name}`,
            cost: model.cost,
            enabled: model.enabled,
            priority: Number(model.priority ?? 999)  // 添加优先级字段，默认最低优先级（数值越小优先级越高：1 最高）
          }))
          .sort((a: any, b: any) => {
            // 按优先级从高到低排序（1 为最高，其次 2、3...）
            const priorityA = Number(a.priority ?? 999)
            const priorityB = Number(b.priority ?? 999)
            return priorityA - priorityB
          })

      // 调试输出排序结果
      console.log('📑 [视频页面] 排序后的模型顺序:', allModels.map((m:any) => `${m.providerName}:${m.name}#${m.priority}`).join(' | '))
      
      console.log(`✅ [视频页面] 模型配置加载成功: ${allModels.length} 个启用模型 (总共 ${models.length} 个)`)
      
      setVideoModels(allModels)
      
      // 归一化当前选择：将旧格式（仅模型名）转换为 provider-model，优先取最高优先级
      if (allModels.length > 0) {
        let needSetDefault = false
        if (selectedModel) {
          const exists = allModels.some((m: any) => m.id === selectedModel)
          if (!exists) {
            if (!String(selectedModel).includes('-')) {
              const fallback = allModels.find((m: any) => m.name === selectedModel)
              if (fallback) {
                console.log('🔧 [视频页面] 归一化旧模型值 ->', selectedModel, '→', `${fallback.id} (${fallback.name} · ${fallback.providerName})`)
                setSelectedModel(fallback.id)
              } else {
                needSetDefault = true
              }
            } else {
              needSetDefault = true
            }
          }
        } else {
          needSetDefault = true
        }

        if (needSetDefault) {
          const top = allModels[0]
          setSelectedModel(top.id)
          console.log(`🔧 [视频页面] 设置默认模型: ${top.id} (${top.name} · ${top.providerName})`)
        }
      } else if (allModels.length === 0) {
        console.warn('⚠️ [视频页面] 没有启用的模型')
        setSelectedModel('')
      }
      } else {
        console.warn('⚠️ [视频页面] 后端无服务商配置，请管理员在控制台配置API服务商')
        setVideoModels([])
        setSelectedModel('')
      }
    } catch (error) {
      console.error('❌ [视频页面] 加载服务商配置失败:', error)
      // 失败时不使用默认配置，完全依赖管理控制台配置
      setVideoModels([])
      setSelectedModel('')
    } finally {
      setLoadingProviders(false)
    }
  }


  // 从localStorage加载用户设置
  useEffect(() => {
    const savedSettings = localStorage.getItem('videoGenerationSettings')
    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings)
        if (settings.selectedModel) setSelectedModel(settings.selectedModel)
        if (settings.orientation) setOrientation(settings.orientation)
        if (settings.duration) setDuration(settings.duration)
        if (settings.prompt) {
          setPromptValue(settings.prompt)
          // 同步到 form 字段，确保表单验证时能获取到值
          try {
            form.setFieldsValue({ prompt: settings.prompt })
          } catch (err) {
            console.warn('设置 form prompt 字段失败:', err)
          }
        }
      } catch (error) {
        console.error('Failed to load saved settings:', error)
      }
    }
  }, [])

  // 加载当前用户的角色列表
  const loadCharacters = async () => {
    try {
      const res = await apiClient.get('/video/characters')
      if (res.success && res.data) {
        // 🔥 合并角色：公共角色在前，用户角色在后
        const allChars = [
          ...(res.data.globalCharacters || []),
          ...(res.data.myCharacters || [])
        ]
        // 如果后端直接返回了 characters 字段，优先使用，但需要重新排序
        let characters = res.data.characters || allChars
        // 🔥 确保排序：公共角色在前（isGlobal=true），用户角色在后（isGlobal=false）
        characters = characters.sort((a: any, b: any) => {
          // 公共角色（isGlobal=true）排在前面
          if (a.isGlobal && !b.isGlobal) return -1
          if (!a.isGlobal && b.isGlobal) return 1
          // 同类型角色保持原有顺序（按 createdAt 排序，由后端保证）
          return 0
        })
        console.log('📋 [角色列表] 加载成功:', {
          total: characters.length,
          global: res.data.globalCharacters?.length || 0,
          my: res.data.myCharacters?.length || 0
        })
        setAvailableCharacters(characters)
      }
    } catch (error) {
      console.error('❌ [视频页面] 加载角色列表失败:', error)
    }
  }

  // 加载服务商配置
  useEffect(() => {
    loadProviders()
    loadCharacters() // 同时加载角色列表
  }, [])

  // 🔥 添加防抖机制：记录上次刷新时间，避免频繁刷新
  const lastRefreshTimeRef = useRef(0)
  const MIN_REFRESH_INTERVAL = 5000 // 最小刷新间隔：5秒
  
  // 加载热门创作灵感
  useEffect(() => {
    loadInspirations()
    
    // 每30秒自动刷新灵感
    const refreshInterval = setInterval(() => {
      const now = Date.now()
      // 🔥 检查是否在最小刷新间隔内
      if (now - lastRefreshTimeRef.current >= MIN_REFRESH_INTERVAL) {
        console.log('🔄 [视频页面] 自动刷新灵感')
        lastRefreshTimeRef.current = now
        loadInspirations()
      } else {
        console.log('⏸️ [视频页面] 跳过刷新，距离上次刷新时间过短')
      }
    }, 30000)
    
    return () => clearInterval(refreshInterval)
  }, [])

  // 当页面重新可见时刷新灵感
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const now = Date.now()
        // 🔥 检查是否在最小刷新间隔内，避免频繁刷新
        if (now - lastRefreshTimeRef.current >= MIN_REFRESH_INTERVAL) {
          console.log('👀 [视频页面] 页面重新可见，刷新灵感')
          lastRefreshTimeRef.current = now
          loadInspirations()
        } else {
          console.log('⏸️ [视频页面] 页面可见但跳过刷新，距离上次刷新时间过短')
        }
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  const loadInspirations = async () => {
    // 🔥 如果正在加载中，跳过本次请求，避免并发请求
    if (loadingInspirations) {
      console.log('⏸️ [视频页面] 灵感正在加载中，跳过重复请求')
      return
    }
    
    try {
      setLoadingInspirations(true)
      const response = await apiClient.get('/inspiration/public', {
        params: {
          type: 'VIDEO',
          limit: 6
        }
      })
      
      // apiClient 已经处理过响应，response 就是 {success, data} 格式
      if (response.success && response.data && Array.isArray(response.data) && response.data.length > 0) {
        const items = response.data.map((item: any) => ({
          ...item,
          tags: typeof item.tags === 'string' ? JSON.parse(item.tags) : item.tags
        }))
        setInspirations(items)
        console.log('✅ [视频页面] 灵感加载成功:', items.length, '个')
      } else {
        // 数据为空时使用默认示例
        console.warn('⚠️ [视频页面] 后端无灵感数据，使用默认示例')
        setInspirations([
          { id: '1', emoji: '🌅', title: '海滩日落', prompt: '日落时分的海滩，海浪轻拍沙滩，海鸥飞过，金色阳光洒在水面上，电影级慢镜头' },
          { id: '2', emoji: '🌃', title: '未来城市', prompt: '未来城市夜景，霓虹灯闪烁，飞行汽车穿梭，赛博朋克风格，雨夜反光' },
          { id: '3', emoji: '🐼', title: '可爱熊猫', prompt: '一只可爱的小熊猫在竹林里玩耍，咀嚼竹叶，憨态可掬，4K高清，自然纪录片风格' },
          { id: '4', emoji: '📹', title: '街头vlog', prompt: '城市街头手持镜头行走拍摄，快速剪辑，转场流畅，音乐节奏感强' },
          { id: '5', emoji: '🌸', title: '樱花飘落', prompt: '樱花树下，花瓣随风飘落，阳光透过树叶洒下光斑，唯美日系风格' }
        ])
      }
    } catch (error) {
      console.error('❌ [视频页面] 加载灵感失败:', error)
      // 失败时也使用默认示例
      setInspirations([
        { id: '1', emoji: '🌅', title: '海滩日落', prompt: '日落时分的海滩，海浪轻拍沙滩，海鸥飞过，金色阳光洒在水面上，电影级慢镜头' },
        { id: '2', emoji: '🌃', title: '未来城市', prompt: '未来城市夜景，霓虹灯闪烁，飞行汽车穿梭，赛博朋克风格，雨夜反光' },
        { id: '3', emoji: '🐼', title: '可爱熊猫', prompt: '一只可爱的小熊猫在竹林里玩耍，咀嚼竹叶，憨态可掬，4K高清，自然纪录片风格' },
        { id: '4', emoji: '📹', title: '街头vlog', prompt: '城市街头手持镜头行走拍摄，快速剪辑，转场流畅，音乐节奏感强' },
        { id: '5', emoji: '🌸', title: '樱花飘落', prompt: '樱花树下，花瓣随风飘落，阳光透过树叶洒下光斑，唯美日系风格' }
      ])
    } finally {
      setLoadingInspirations(false)
    }
  }

  // 保存用户设置到localStorage
  const saveSettings = () => {
    const settings = {
      selectedModel,
      orientation,
      duration,
      prompt: promptValue
    }
    localStorage.setItem('videoGenerationSettings', JSON.stringify(settings))
  }

  // 当设置改变时保存
  useEffect(() => {
    saveSettings()
  }, [selectedModel, orientation, duration])
  
  // 定期检查生成任务状态（备用机制）
  const errorCountRef = useRef(0)
  // 🔥 使用 ref 存储最新的 currentGeneration，避免闭包问题
  const currentGenerationRef = useRef<any>(null)
  
  // 同步 currentGeneration 到 ref
  useEffect(() => {
    currentGenerationRef.current = currentGeneration
  }, [currentGeneration])
  
  // 检测是否为移动设备
  const isMobileDevice = () => {
    return /Mobile|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
  }
  
  // 检测是否为iOS设备
  const isIOSDevice = () => {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent)
  }
  
  useEffect(() => {
    // 🔥 修复：失败状态应该立即停止轮询，避免任何副作用
    // 检查条件：无任务、已完成、已失败、有输出视频
    if (!currentGeneration || 
        currentGeneration.status?.toLowerCase() === 'completed' || 
        currentGeneration.status?.toLowerCase() === 'failed' || 
        currentGeneration.outputVideo) {
      console.log('🛑 [定期检查] 停止轮询，状态:', currentGeneration?.status)
      // 重置错误计数
      errorCountRef.current = 0
      return
    }
    
    const MAX_ERROR_COUNT = 3 // 连续3次错误后停止轮询
    
    const checkStatus = async () => {
      try {
        console.log('🔄 [定期检查] 查询任务状态:', currentGeneration.id)
        const response = await apiClient.get(`/video/generations/${currentGeneration.id}`)
        
        // 重置错误计数（成功时）
        errorCountRef.current = 0
        
        if (response.success && response.data?.videoGeneration) {
          const gen = response.data.videoGeneration
          console.log('📊 [定期检查] 任务状态:', gen.status)
          console.log('📊 [定期检查] 错误信息:', gen.errorMessage)
          
          if (gen.status?.toLowerCase() === 'completed' && gen.outputVideo) {
            console.log('✅ [定期检查] 任务已完成，更新状态')
            setCurrentGeneration({
              ...currentGeneration,
              status: 'completed',
              outputVideo: gen.outputVideo
            })
          } else if (gen.status?.toLowerCase() === 'failed') {
            console.log('❌ [定期检查] 任务失败，更新状态')
            console.log('❌ [定期检查] 错误信息:', gen.errorMessage)
            setCurrentGeneration({
              ...currentGeneration,
              status: 'failed',
              errorMessage: gen.errorMessage
            })
            
            // 显示友好的错误提示
            if (gen.errorMessage && (
              gen.errorMessage.includes('人物照片') ||
              gen.errorMessage.includes('内容审核') ||
              gen.errorMessage.includes('photorealistic people')
            )) {
              console.log('📢 [定期检查] 显示内容审核错误提示（内联）:', gen.errorMessage)
              console.log('📢 [定期检查] 设置 inlineError 状态:', gen.errorMessage)
              setInlineError(gen.errorMessage)
            } else {
              console.log('📢 [定期检查] 显示通用错误提示:', gen.errorMessage || '视频生成失败')
              message.error({
                content: gen.errorMessage || '视频生成失败',
                key: `error-${currentGeneration.id}`
              })
            }
          }
        }
      } catch (error: any) {
        errorCountRef.current++
        console.error(`❌ [定期检查] 查询失败 (${errorCountRef.current}/${MAX_ERROR_COUNT}):`, error)
        
        // 检查是否是服务器错误（500, 502, 503等）
        const isServerError = error?.response?.status >= 500 && error?.response?.status < 600
        
        if (errorCountRef.current >= MAX_ERROR_COUNT) {
          console.error(`🛑 [定期检查] 连续${MAX_ERROR_COUNT}次错误，停止轮询`)
          if (isServerError) {
            message.warning({
              content: '服务器暂时无法响应，请稍后刷新页面查看状态',
              key: `server-error-${currentGeneration.id}`,
              duration: 5
            })
          }
          return // 停止定时器
        }
        
        // 如果是服务器错误，显示警告（但不阻止轮询，直到达到最大错误次数）
        if (isServerError && errorCountRef.current === 1) {
          console.warn('⚠️  [定期检查] 服务器错误，将减少轮询频率')
        }
      }
    }
    
    // 每10秒检查一次
    const timer = setInterval(() => {
      checkStatus().then(() => {
        // 如果错误次数达到上限，清除定时器
        if (errorCountRef.current >= MAX_ERROR_COUNT) {
          clearInterval(timer)
        }
      })
    }, 10000)
    
    return () => clearInterval(timer)
  }, [currentGeneration])
  // 实时更新当前生成任务状态（若打开详情时）
  useEffect(() => {
    const handler = (p: any) => {
      // 🔥 使用 ref 获取最新的 currentGeneration，避免闭包问题
      const currentGen = currentGenerationRef.current
      console.log('📺 [视频生成页面] 收到视频更新事件:', p)
      console.log('🔍 [SSE] 当前任务ID:', currentGen?.id, '事件任务ID:', p.id)
      console.log('🔍 [SSE] 事件状态:', p.status, '错误信息:', p.errorMessage)
      
      if (currentGen?.id === p.id) {
        console.log('✅ [视频生成页面] 匹配到当前生成任务，更新状态')
        console.log('📊 [视频生成页面] 事件数据:', { 
          status: p.status, 
          outputVideo: p.outputVideo,
          hasOutputVideo: !!p.outputVideo,
          errorMessage: p.errorMessage
        })
        
        setCurrentGeneration(prev => {
          const updated = { 
            ...prev, 
            ...p,
            // 确保状态是小写（统一格式）
            status: p.status?.toLowerCase() || prev.status,
            // 确保错误信息正确传递
            errorMessage: p.errorMessage || prev.errorMessage
          }
          console.log('🔄 [视频生成页面] 更新后的状态:', updated)
          
          // 如果是失败状态，显示友好的错误提示
          if (updated.status === 'failed' && p.errorMessage) {
            console.log('📢 [SSE事件] 检测到失败状态，错误信息:', p.errorMessage)
            console.log('📢 [SSE事件] 更新后的状态:', updated.status)
            // 🔥 任务失败时清除loading状态和按钮文本状态
            setLoading(false)
            setButtonTextState(null)
            if (p.errorMessage.includes('人物照片') || 
                p.errorMessage.includes('内容审核') ||
                p.errorMessage.includes('photorealistic people')) {
              console.log('📢 [SSE事件] 显示内容审核错误提示（内联）:', p.errorMessage)
              console.log('📢 [SSE事件] 设置 inlineError 状态:', p.errorMessage)
              setInlineError(p.errorMessage)
            } else {
              console.log('📢 [SSE事件] 显示通用错误提示:', p.errorMessage || '视频生成失败')
              message.error({
                content: p.errorMessage || '视频生成失败',
                key: `error-${p.id}`
              })
            }
          } else if (updated.status === 'completed' && updated.outputVideo) {
            // 🔥 任务完成时清除loading状态和按钮文本状态
            console.log('✅ [SSE事件] 任务完成，清除loading状态')
            setLoading(false)
            setButtonTextState(null)
          } else {
            console.log('🔍 [SSE事件] 非失败状态或无误信息:', {
              status: updated.status,
              hasErrorMessage: !!p.errorMessage,
              errorMessage: p.errorMessage
            })
          }
          
          return updated
        })
      } else {
        console.log('⚠️  [视频生成页面] 任务ID不匹配', {
          currentId: currentGen?.id,
          eventId: p.id
        })
      }
    }
    onEvent('video_updated', handler)
    
    // 🔥 监听角色创建事件，自动更新角色列表
    const characterHandler = (payload: any) => {
      console.log('🔔 [视频页面] 收到角色创建事件:', payload)
      
      if (payload.status === 'COMPLETED' && payload.character_id) {
        // 角色创建成功，重新加载角色列表
        message.success(`角色 "${payload.name}" 创建成功！`)
        loadCharacters()
        // 如果表单中有数据，清空表单
        if (newCharacterForm.getFieldValue('name') === payload.name) {
          newCharacterForm.resetFields()
        }
      } else if (payload.status === 'FAILED') {
        // 角色创建失败
        message.error(payload.error || `角色 "${payload.name}" 创建失败`)
      }
    }
    onEvent('character_created', characterHandler)
    
    // 🔥 清理函数：移除事件处理器，避免重复注册
    return () => {
      offEvent('video_updated', handler)
      offEvent('character_created', characterHandler)
    }
  }, []) // 移除 currentGeneration 依赖，因为使用 ref

  // 删除参考图（支持多图，默认删除指定索引，不传则清空全部）
  const handleRemoveImage = (index?: number) => {
    if (index === undefined) {
      setRefImages([])
    } else {
      setRefImages(prev => prev.filter((_, i) => i !== index))
    }
    setInlineError(null) // 清空内联错误提示
    message.success('已移除参考图')
  }

  // 🔥 当 refImages 变化时，同步旧的单图状态，兼容现有校验和非 Veo3 逻辑
  useEffect(() => {
    if (refImages.length > 0) {
      const first = refImages[0]
      setRefImageFile(first.file)
      setRefImageUrl(first.url)
      setRefImagePreviewUrl(first.previewUrl)
      setGenerationType('IMAGE_TO_VIDEO')
    } else {
      setRefImageFile(null)
      setRefImageUrl(null)
      setRefImagePreviewUrl(null)
      setGenerationType('TEXT_TO_VIDEO')
    }
  }, [refImages])

  // 处理模型配置，添加UI相关属性
  const getModelWithUI = (model: any) => {
    if (!model) return null
    
    const icons: Record<string, any> = {
      'SORA2': <RocketOutlined />,
      'SORA2_PRO': <FireOutlined />,
      'SORA2_ADVANCED': <CrownOutlined />
    }
    const colors: Record<string, string> = {
      'SORA2': '#3b82f6',
      'SORA2_PRO': '#f59e0b',
      'SORA2_ADVANCED': '#8b5cf6'
    }
    const tags: Record<string, { text: string, color: string }> = {
      'SORA2': { text: '推荐', color: 'blue' },
      'SORA2_PRO': { text: '专业', color: 'orange' },
      'SORA2_ADVANCED': { text: '旗舰', color: 'purple' }
    }
    
    return {
      ...model,
      icon: icons[model.id] || <RocketOutlined />,
      color: colors[model.id] || '#3b82f6',
      tag: tags[model.id]?.text,
      tagColor: tags[model.id]?.color
    }
  }

  const currentModelInfo = getModelWithUI(videoModels.find(model => model.id === selectedModel))

  // 🔥 新增：计算视频生成成本
  const calculateVideoCost = (): number => {
    const selectedModelData = videoModels.find(m => m.id === selectedModel);
    if (!selectedModelData) return 20; // 默认成本
    
    let cost = selectedModelData.cost || 20;
    
    // 🔥 计算参考图额外积分（首尾帧或3张参考图）
    const isVeo3Model = selectedModel && selectedModel.toLowerCase().startsWith('veo3-');
    if (isVeo3Model && refImages.length > 0) {
      if (refImages.length === 2) {
        // 首尾帧模式：额外 +10 积分
        cost += 10;
      } else if (refImages.length >= 3) {
        // 3张参考图模式：额外 +15 积分
        cost += 15;
      } else if (refImages.length === 1) {
        // 1张参考图（urls模式）：额外 +5 积分
        cost += 5;
      }
    } else if (refImageUrl && refImageUrl.trim() !== '') {
      // 单张参考图（传统模式）：额外 +5 积分
      cost += 5;
    }
    
    // 🔥 添加角色使用积分
    if (characters && characters.length > 0 && availableCharacters.length > 0) {
      const characterUseCost = characters.reduce((sum: number, selectedChar: any) => {
        const char = availableCharacters.find(
          (c) => c.videoUrl === selectedChar.url && c.timestamps === selectedChar.timestamps
        );
        return sum + (char?.useCost || 0);
      }, 0);
      cost += characterUseCost;
    }
    
    return cost;
  };

  // 打开/关闭角色弹窗时，同步已选择的角色
  const handleOpenCharacterModal = async () => {
    await loadCharacters()
    // 根据当前 characters 状态反推选中的角色 ID（按 URL+timestamps 匹配）
    const ids = availableCharacters
      .filter((c) =>
        characters.some((ch) => ch.url === c.videoUrl && ch.timestamps === c.timestamps)
      )
      .map((c) => c.id)
    setSelectedCharacterIds(ids)
    setCharacterModalVisible(true)
  }

  const handleConfirmCharacters = () => {
    // 根据选中的角色ID生成 characters 数组（url + timestamps）
    const selected = availableCharacters.filter((c) =>
      selectedCharacterIds.includes(c.id)
    )
    const mapped = selected.map((c) => ({
      url: c.videoUrl,
      timestamps: c.timestamps
    }))
    setCharacters(mapped)
    updatePromptWithCharacters(mapped)
    setCharacterModalVisible(false)
  }

  // 更新提示词，添加或移除角色引用
  const updatePromptWithCharacters = (selectedCharacters: Array<{ url: string; timestamps: string }>) => {
    let currentPrompt = promptValue || ''
    
    // 移除所有现有的角色引用（@character1, @character2 等）及其前后的空格
    currentPrompt = currentPrompt.replace(/\s*@character\d+\s*/g, ' ').trim()
    
    // 移除所有已存在的角色中文名称（带 @ 符号和不带 @ 符号的都要移除）
    availableCharacters.forEach((char) => {
      // 移除带 @ 符号的角色名称
      const nameWithAtPattern = new RegExp(`\\s*@${char.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*`, 'g')
      currentPrompt = currentPrompt.replace(nameWithAtPattern, ' ')
      // 移除不带 @ 符号的角色名称（向后兼容）
      const namePattern = new RegExp(`\\s*${char.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*`, 'g')
      currentPrompt = currentPrompt.replace(namePattern, ' ')
    })
    
    // 添加新的角色引用（使用中文名称，带 @ 符号）
    if (selectedCharacters.length > 0) {
      // 根据选中的角色，从 availableCharacters 中找到对应的角色名称
      const characterNames = selectedCharacters
        .map((selectedChar) => {
          const char = availableCharacters.find(
            (c) => c.videoUrl === selectedChar.url && c.timestamps === selectedChar.timestamps
          )
          return char ? `@${char.name}` : null
        })
        .filter((name) => name !== null)
      
      if (characterNames.length > 0) {
        // 每个角色名称后面添加空格，最后也保留空格
        const characterRefs = characterNames.map(name => `${name} `).join('')
        // 如果提示词不为空，在末尾添加角色引用（角色引用后面有空格）；如果为空，直接添加
        if (currentPrompt) {
          currentPrompt = `${currentPrompt} ${characterRefs}`
        } else {
          currentPrompt = characterRefs
        }
      }
    }
    
    // 清理多余的空格（多个连续空格替换为单个空格）
    currentPrompt = currentPrompt.replace(/\s+/g, ' ')
    
    // 如果有选中的角色，确保角色引用后面有空格
    if (selectedCharacters.length > 0) {
      // 先trim掉末尾空格
      currentPrompt = currentPrompt.trim()
      // 然后在末尾添加一个空格（角色引用后面需要空格）
      currentPrompt = currentPrompt + ' '
    } else {
      // 没有角色时，正常trim
      currentPrompt = currentPrompt.trim()
    }
    
    // 更新提示词
    setPromptValue(currentPrompt)
    try {
      form.setFieldsValue({ prompt: currentPrompt })
    } catch {}
    
    console.log('📝 [角色选择] 提示词已更新:', {
      selectedCount: selectedCharacters.length,
      newPrompt: currentPrompt
    })
  }

  const handleCreateCharacter = async () => {
    try {
      const values = await newCharacterForm.validateFields()
      setCreatingCharacter(true)

      const payload = {
        name: values.name,
        url: values.url,
        timestamps: values.timestamps
      }

      console.log('📤 [前端] 创建角色请求:', payload)
      const res = await apiClient.post('/video/upload-character', payload)

      console.log('📥 [前端] 创建角色响应:', res)

      if (res.success) {
        if (res.data?.status === 'PROCESSING') {
          // 🔥 异步处理中，立即关闭表单并显示提示（30秒后自动关闭）
          message.loading({
            content: `角色 "${values.name}" 上传中，处理完成后会自动添加到角色列表...`,
            duration: 30, // 30秒后自动关闭
            key: 'character_uploading'
          })
          // 立即关闭表单，等待 SSE 事件通知
          setCharacterModalVisible(false)
          newCharacterForm.resetFields()
        } else if (res.data?.character) {
          // 同步返回（兼容旧版本）
          message.success('角色创建成功')
          setAvailableCharacters((prev) => [res.data.character, ...prev])
          setCharacterModalVisible(false)
          newCharacterForm.resetFields()
        } else {
          message.success('角色上传任务已提交，正在处理中...')
          setCharacterModalVisible(false)
          newCharacterForm.resetFields()
        }
      } else {
        message.error(res.message || '角色创建失败')
      }
    } catch (error: any) {
      if (error?.errorFields) {
        // 表单校验错误，不提示
        return
      }
      console.error('❌ [前端] 创建角色失败:', error)
      if (error?.response?.data) {
        console.error('📡 [前端] 创建角色失败响应:', error.response.data)
      }
      const backendMsg = error?.response?.data?.message
      message.error(backendMsg || error?.message || '角色创建失败')
    } finally {
      setCreatingCharacter(false)
    }
  }

  const handleGenerate = async (values: any) => {
    const now = Date.now();
    const timeSinceLastClick = now - lastClickTimeRef.current;
    
    // 🔥 5秒防重复提交保护：检查是否在最小间隔内
    if (timeSinceLastClick < MIN_CLICK_INTERVAL && lastClickTimeRef.current > 0) {
      const remainingTime = Math.ceil((MIN_CLICK_INTERVAL - timeSinceLastClick) / 1000);
      message.warning(`请稍候 ${remainingTime} 秒后再试，防止重复提交`);
      return;
    }
    
    // 🔥 修改：允许异步处理视频生成，无需等待当前任务完成
    // 移除"当前有正在进行的生成任务"的检查，允许同时有多个生成任务
    
    // 🔥 修复：如果当前是失败状态，清空状态以便重新生成
    // 失败后积分已退还，重新生成时需要正常扣除积分
    if (currentGeneration && currentGeneration.status?.toLowerCase() === 'failed') {
      console.log('🔄 [重新生成] 检测到失败状态，清空当前生成记录以便重新生成')
      setCurrentGeneration(null)
      // 清空内联错误提示
      setInlineError(null)
    }
    
    // 更新最后点击时间
    lastClickTimeRef.current = now;
    
    // 🔥 立即设置loading状态，按钮显示"生成中..."
    setLoading(true)
    
    // 用于标记任务是否创建成功
    let taskCreated = false
    
    const startTime = Date.now();
    console.log('\n' + '='.repeat(80));
    console.log('📹 [前端] 视频生成请求开始');
    console.log('⏰ 时间:', new Date().toISOString());
    console.log('='.repeat(80));
    
    // 清空内联错误提示
    setInlineError(null)
    
    // 注释：全局错误监听器已移除，避免与 SSE 事件处理器重复
    
    // 🔥 前端验证：检查提示词长度
    const promptValue = values.prompt || '';
    if (typeof promptValue !== 'string' || promptValue.trim().length < 5) {
      const currentLength = promptValue.trim().length;
      message.warning({
        content: `提示词至少需要5个字符，当前只有${currentLength}个字符，请补充更多描述`,
        duration: 3
      });
      console.warn(`⚠️ [前端验证] 提示词太短: ${currentLength} 个字符`);
      return;
    }
    
    if (promptValue.length > 2000) {
      message.warning({
        content: `提示词最多2000个字符，当前有${promptValue.length}个字符，请缩短描述`,
        duration: 3
      });
      console.warn(`⚠️ [前端验证] 提示词太长: ${promptValue.length} 个字符`);
      return;
    }
    
    // 🔥 新增：检查积分是否足够
    const { user } = useAuthStore.getState();
    const cost = calculateVideoCost();
    
    if (!user || (user.pointsBalance < cost && user.credits < cost)) {
      message.error(`积分不足！需要 ${cost} 积分，当前余额: ${user?.pointsBalance || 0} 积分`);
      console.error(`❌ 积分不足: 需要 ${cost}, 当前 ${user?.pointsBalance || 0}`);
      return;
    }
    
    console.log(`✅ 积分检查通过: 需要 ${cost}, 当前 ${user.pointsBalance}`);
    console.log(`💰 [积分处理] 重新生成时将正常扣除 ${cost} 积分（失败任务的积分已退还）`);
    
    if (generationType === 'IMAGE_TO_VIDEO' && !refImageUrl) {
      console.warn('⚠️  [验证失败] 图生视频需要参考图片');
      message.warning('请上传参考图片')
      return
    }
    
    // 验证 refImageUrl 格式（如果是图生视频类型）
    if (generationType === 'IMAGE_TO_VIDEO' && refImageUrl) {
      try {
        const url = new URL(refImageUrl)
        if (!url.protocol.startsWith('http')) {
          console.error('❌ [验证失败] refImageUrl 不是有效的 HTTP(S) URL:', refImageUrl)
          message.error('参考图 URL 格式错误，请重新上传')
          return
        }
      } catch (e) {
        console.error('❌ [验证失败] refImageUrl 不是有效的 URL:', refImageUrl)
        message.error('参考图 URL 格式错误，请重新上传')
        return
      }
    }
    
    setLoading(true)
    
    try {
      // 将提示词中的 @角色名称 转换为 @character1 格式（用于API调用）
      let apiPrompt = promptValue || values.prompt || ''
      if (characters && characters.length > 0) {
        // 根据选中的角色顺序，将 @角色名称 替换为 @character1, @character2 等
        characters.forEach((selectedChar, index) => {
          const char = availableCharacters.find(
            (c) => c.videoUrl === selectedChar.url && c.timestamps === selectedChar.timestamps
          )
          if (char) {
            // 替换 @角色名称 为 @characterX 格式
            const namePattern = new RegExp(`@${char.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g')
            apiPrompt = apiPrompt.replace(namePattern, `@character${index + 1}`)
          }
        })
      }
      
      const payload: any = {
        prompt: apiPrompt,
        model: selectedModel,
        type: generationType,
        orientation,
        hasAudio,
        duration
      }
      
      // 🔥 参考图参数映射：对 Veo3 使用 1/2/3 张图的自动模式，其它模型保持单图 inputImage 逻辑
      const isVeo3Model = selectedModel && selectedModel.toLowerCase().startsWith('veo3-')

      if (isVeo3Model && refImages.length > 0) {
        const urls = refImages.map(img => img.url)
        if (urls.length === 1) {
          // 1 张图：urls 模式
          payload.refImageUrls = [urls[0]]
          console.log('📸 [前端] Veo3 参考图模式: 1 张 urls', payload.refImageUrls)
        } else if (urls.length === 2) {
          // 2 张图：首尾帧模式
          payload.firstFrameUrl = urls[0]
          payload.lastFrameUrl = urls[1]
          console.log('📸 [前端] Veo3 参考图模式: 首尾帧', { firstFrameUrl: urls[0], lastFrameUrl: urls[1] })
        } else {
          // 3 张及以上：多图 urls（最多3张）
          payload.refImageUrls = urls.slice(0, 3)
          console.log('📸 [前端] Veo3 参考图模式: 多图 urls', payload.refImageUrls)
        }
      } else if (refImageUrl && refImageUrl.trim() !== '') {
        // 非 Veo3 或无多图时，继续使用原有 inputImage 逻辑
        payload.inputImage = refImageUrl
        console.log('📸 [前端] 包含参考图URL:', refImageUrl);
        console.log('📸 [前端] 参考图URL类型:', typeof refImageUrl);
        console.log('📸 [前端] 参考图URL是否为blob:', refImageUrl.startsWith('blob:'));
      }

      // 如果有角色，添加到payload
      if (characters && characters.length > 0) {
        payload.characters = characters
        console.log('🎭 [前端] 包含角色:', characters);
      }
      
      console.log('📊 请求参数:', {
        model: selectedModel,
        type: generationType,
        orientation,
        hasAudio,
        duration,
        inputImage: refImageUrl || '无',
        promptLength: (promptValue || '').length,
        promptPreview: (promptValue || '').substring(0, 100) + ((promptValue || '').length > 100 ? '...' : '')
      });
      
      console.log('📤 发送 API 请求: POST /video/generate');
      console.log('📦 完整 payload:', JSON.stringify(payload, null, 2));
      const apiStartTime = Date.now();
      
      const res = await apiClient.post('/video/generate', payload)
      const apiTime = Date.now() - apiStartTime;
      
      console.log(`✅ API 响应成功 (耗时: ${apiTime}ms)`);
      console.log('📥 响应数据:', {
        success: res.success,
        hasVideoGeneration: !!res.data?.videoGeneration,
        generationId: res.data?.videoGeneration?.id,
        status: res.data?.videoGeneration?.status,
        cost: res.data?.videoGeneration?.cost
      });
      
      if (res.success && res.data?.videoGeneration) {
        taskCreated = true // 标记任务创建成功
        setCurrentGeneration({...res.data.videoGeneration, status: 'GENERATING'})
        // 🔥 设置按钮状态为"生成中"
        setButtonTextState('generating')
        // 🔥 5秒后将按钮文本改为"预估等待1-5分钟 在历史中查看"
        setTimeout(() => {
          setButtonTextState('waiting')
          message.success('已创建生成任务，正在生成中...')
          
          // 🔥 再10秒后恢复按钮为正常状态，但任务仍在后台生成
          setTimeout(() => {
            setLoading(false)
            setButtonTextState(null)
            console.log('🔄 [按钮状态] 按钮已恢复正常状态，任务仍在后台生成中')
          }, 10000) // 总共15秒后恢复（5秒 + 10秒）
        }, 5000)
        console.log('✅ 生成任务已创建, ID:', res.data.videoGeneration.id);
        
        // 移动端自动切换到结果标签
        setMobileTab('result')
        
        // 本地乐观扣费，SSE 随后会以绝对值校正
        // 🔥 注意：如果是从失败状态重新生成，之前的积分已退还，这里正常扣除新任务的积分
        try {
          const { user, updateUser } = useAuthStore.getState()
          if (user?.pointsBalance !== undefined && (res.data.videoGeneration?.cost !== undefined)) {
            const oldCredits = user.pointsBalance || 0;
            const cost = res.data.videoGeneration.cost || 0;
            const newCredits = Math.max(0, oldCredits - cost);
            updateUser({ pointsBalance: newCredits })
            console.log(`💰 本地积分更新: ${oldCredits} → ${newCredits} (扣除: ${cost})`);
            console.log(`💡 [积分说明] 如果这是从失败任务重新生成，之前的积分已退还，这是新任务的正常扣费`);
          }
        } catch (creditsError) {
          console.error('❌ 本地积分更新失败:', creditsError);
        }
        
        const totalTime = Date.now() - startTime;
        console.log(`🎉 请求处理完成 (总耗时: ${totalTime}ms)`);
        console.log('='.repeat(80) + '\n');
        
        // 🔥 注意：不在这里清除loading，让按钮保持"生成中..."状态
        // loading状态会在任务完成或失败时通过SSE事件清除
        // 如果任务创建失败，会在finally块中清除loading状态
      } else {
        throw new Error(res.message || '生成创建失败')
      }
    } catch (e: any) {
      const totalTime = Date.now() - startTime;
      console.error('\n' + '='.repeat(80));
      console.error('❌ [前端] 视频生成请求失败');
      console.error('⏱️  失败前耗时:', totalTime + 'ms');
      console.error('='.repeat(80));
      
      console.error('🔍 错误详情:');
      console.error('  错误类型:', e?.constructor?.name || 'Unknown');
      console.error('  错误消息:', e?.message || 'Unknown error');
      
      if (e?.response) {
        console.error('📡 API 响应错误:');
        console.error('  状态码:', e.response.status);
        console.error('  状态文本:', e.response.statusText);
        console.error('  响应数据:', e.response.data);
        
        // 如果是验证错误，显示详细的字段错误信息
        if (e.response.status === 400 && e.response.data?.errors) {
          const errorMessages = e.response.data.errors.map((err: any) => 
            `${err.field}: ${err.message}`
          ).join('；');
          message.error(`输入验证失败：${errorMessages}`);
          return;
        }
        
        // 如果有错误消息，显示它
        if (e.response.data?.message) {
          message.error(e.response.data.message);
          return;
        }
      }
      
      if (e?.stack) {
        console.error('📚 错误堆栈:', e.stack);
      }
      
      console.error('='.repeat(80) + '\n');
      
      // 检测积分不足错误（处理 500 错误中的积分不足情况）
      let errorMessage = e?.message || '生成失败，请重试';
      let isInsufficientCredits = false;
      let allMessages: string[] = [];
      
      // 收集所有可能的错误消息
      if (e?.message) {
        allMessages.push(e.message);
      }
      
      if (e?.response?.data?.message) {
        allMessages.push(e.response.data.message);
      }
      
      if (e?.response?.data?.error) {
        allMessages.push(String(e.response.data.error));
      }
      
      // 合并所有消息进行检测
      const combinedMessage = allMessages.join(' ').toLowerCase();
      
      // 检测积分不足关键词
      if (combinedMessage.includes('insufficient credits') ||
          combinedMessage.includes('积分不足') ||
          combinedMessage.includes('credits and points') ||
          (combinedMessage.includes('insufficient') && combinedMessage.includes('credit'))) {
        isInsufficientCredits = true;
      }
      
      // 尝试解析嵌套的 JSON 字符串（如：{"code":-1,"msg":"insufficient credits"}）
      if (!isInsufficientCredits) {
        try {
          const messageStr = allMessages.join(' ');
          const jsonMatch = messageStr.match(/\{[^}]+\}/);
          if (jsonMatch) {
            const nestedJson = JSON.parse(jsonMatch[0]);
            if (nestedJson && nestedJson.msg) {
              const nestedMsg = String(nestedJson.msg).toLowerCase();
              if (nestedMsg.includes('insufficient credits') ||
                  nestedMsg.includes('积分不足') ||
                  nestedMsg.includes('credit')) {
                isInsufficientCredits = true;
              }
            }
          }
        } catch (parseError) {
          // 解析失败，继续使用原始消息
        }
      }
      
      // 显示错误提示
      if (isInsufficientCredits) {
        Modal.confirm({
          title: '积分不足',
          content: '您的积分不足，无法生成视频。是否前往充值？',
          okText: '去充值',
          cancelText: '取消',
          onOk: () => {
            // 导航到充值页面
            navigate('/payment');
          }
        });
      } else {
        message.error(errorMessage);
      }
    } finally {
      // 🔥 只有在任务创建失败时才清除loading状态和按钮文本状态
      // 如果任务创建成功，保持loading状态，直到任务完成或失败（通过SSE事件清除）
      if (!taskCreated) {
        setLoading(false)
        setButtonTextState(null)
      }
    }
  }

  return (
    <>
    <div className="min-h-screen w-full relative">
      {/* 内容区域 - 应用统一样式 */}
      <div className="unified-container page-with-fixed-button relative z-10" style={{
        paddingTop: 'var(--spacing-sm)'
      }}>
          <motion.div
          {...COMMON_STYLES.pageEnter}
          transition={{ duration: 0.8, ease: "easeOut" }}
            className="flex flex-col"
          >
          {/* 头部设计 - 左右结构 */}
          <div className="w-full page-header-section">
            <motion.div
              {...COMMON_STYLES.pageEnter}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col md:flex-row md:items-center md:justify-between"
              style={{
                marginBottom: 'var(--spacing-lg)',
                gap: 'var(--spacing-md)'
              }}
            >
              {/* 左侧：标题和副标题 */}
              <div className="flex-1">
                <div className="flex items-center" style={{
                  gap: 'var(--spacing-md)',
                  marginBottom: 'var(--spacing-sm)'
                }}>
                  <span style={{ fontSize: 'var(--font-h1-mobile)' }}>🎬</span>
                  <h1 className="unified-title-h1 mb-0 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                    AI 视频生成
                  </h1>
                </div>
                <Paragraph className="unified-text-secondary" style={{
                  fontSize: 'var(--font-body)',
                  marginLeft: 'var(--spacing-xxl)',
                  marginBottom: 0
                }}>
                  用文字描述你的创意，让AI为你创造惊艳的视频内容
                </Paragraph>
              </div>
              
              {/* 右侧：特色标签 */}
              <div className="flex flex-wrap md:justify-end" style={{ gap: 'var(--spacing-sm)' }}>
                <Tag icon={<ThunderboltOutlined />} className="unified-tag bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700">
                秒级生成
              </Tag>
                <Tag icon={<FireOutlined />} className="unified-tag bg-gradient-to-r from-orange-50 to-orange-100 text-orange-700">
                高清画质
              </Tag>
                <Tag icon={<CheckCircleOutlined />} className="unified-tag bg-gradient-to-r from-green-50 to-green-100 text-green-700">
                无需技能
              </Tag>
              </div>
            </motion.div>
          </div>


          {/* 主内容区 - 左右2:1布局 */}
          <div className="w-full h-full">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch h-full">
              {/* 左侧：输入窗口 - 占2列 (2/3) */}
              <div className="lg:col-span-2">
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="bg-white shadow-lg border border-gray-100 sticky top-6 unified-card"
                  style={{
                    borderRadius: 'var(--radius-xl)'
                  }}
                >
                {/* 移动端顶部装饰 */}
                <div className="md:hidden bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 h-1.5 w-full rounded-full mb-4 md:mb-6"></div>

                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleGenerate}
                  className="flex flex-col"
                >
                  {/* 参考图预览卡片 - 功能暂未实现，已隐藏 */}
                  {false && refImageFile && (
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 shadow-md hover:shadow-lg transition-all duration-300" style={{
                      marginBottom: 'var(--spacing-lg)',
                      padding: 'var(--spacing-lg)',
                      borderRadius: 'var(--radius-xl)'
                    }}>
                      <div className="flex items-center justify-between" style={{ marginBottom: 'var(--spacing-sm)' }}>
                        <Text className="font-bold text-blue-700 flex items-center" style={{
                          fontSize: 'var(--font-small)',
                          gap: 'var(--spacing-sm)'
                        }}>
                          📸 参考图片
                        </Text>
                        <div className="flex items-center gap-1 px-2 py-1 bg-green-500 text-white rounded-full text-xs font-medium">
                          <span>✓</span>
                          <span>已上传</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="relative w-20 h-20 flex-shrink-0">
                          <img 
                            src={refImagePreviewUrl || URL.createObjectURL(refImageFile)} 
                            alt="参考图" 
                            className="w-full h-full object-cover rounded-xl shadow-md"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <Text className="block text-sm font-semibold truncate text-gray-800">{refImageFile.name}</Text>
                          <Text type="secondary" className="text-xs text-gray-500">
                            {(refImageFile.size / 1024).toFixed(2)} KB
                          </Text>
                          {refImageUrl && (
                            <Text type="secondary" className="text-xs text-green-600 block truncate" title={refImageUrl}>
                              ✓ 已上传到云端
                            </Text>
                          )}
                        </div>
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            // 清理blob URL防止内存泄漏
                            if (refImagePreviewUrl) {
                              URL.revokeObjectURL(refImagePreviewUrl);
                            }
                            setRefImageFile(null);
                            setRefImageUrl(null);
                            setRefImagePreviewUrl(null);
                            setGenerationType('TEXT_TO_VIDEO');
                          }}
                          className="rounded-xl bg-red-50 border-red-200 text-red-600 hover:bg-red-100 hover:border-red-300 transition-all duration-200 shadow-sm"
                        >
                          删除
                        </Button>
                              </div>
                            </div>
                  )}

                  {/* 热门创作灵感卡片 - 从API动态加载 */}
                  <div className="shadow-md hover:shadow-lg transition-all duration-300 relative" style={{
                    marginBottom: 'var(--spacing-md)',
                    padding: '2px',
                    borderRadius: 'var(--radius-xl)',
                    // 边框渐变：从右到左
                    background: 'linear-gradient(to left, rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.1))'
                  }}>
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 w-full h-full flex flex-col" style={{
                      paddingTop: '12px',
                      paddingBottom: '12px',
                      paddingLeft: '16px',
                      paddingRight: '16px',
                      borderRadius: 'calc(var(--radius-xl) - 2px)',
                      gap: '10px'
                    }}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center" style={{ gap: '8px' }}>
                          <BulbOutlined className="text-lg text-blue-600" />
                          <Text className="text-sm font-bold text-blue-700">热门创作灵感</Text>
                          {inspirations.length > 0 && (
                            <Tag color="blue" className="text-xs">{inspirations.length}个</Tag>
                          )}
                        </div>
                        <Text className="text-xs text-blue-600">点击即可使用 →</Text>
                      </div>
                      <div className="flex overflow-x-auto relative z-10 inspiration-scroll inspiration-scroll-blue hide-scrollbar" style={{ gap: '4px' }}>
                        {inspirations.length > 0 ? (
                          inspirations.map((item: any) => (
                            <button
                        key={item.id}
                            type="button"
                            role="button"
                            tabIndex={0}
                            className="inline-flex items-center px-2 md:px-2.5 py-1 md:py-1.5 text-xs text-gray-700 hover:text-blue-700 cursor-pointer transition-all duration-200 font-medium hover:scale-105 relative z-10 pointer-events-auto whitespace-nowrap overflow-hidden text-ellipsis max-w-[180px] md:max-w-[200px] flex-shrink-0"
                            onClick={async () => {
                              setMobileTab('create')
                              setPromptValue(item.prompt)
                              try { form.setFieldsValue({ prompt: item.prompt }) } catch {}
                              try { form.scrollToField('prompt', { block: 'center' }) } catch {}
                              setTimeout(() => { try { promptInputRef?.current?.focus?.() } catch {} }, 0)
                              message.success(`已应用"${item.title}"灵感 ✨`)
                              // 记录使用次数
                              try {
                                await apiClient.post(`/inspiration/${item.id}/use`)
                              } catch (error) {
                                console.error('记录使用失败:', error)
                              }
                            }}
                              title={`${item.title}\n\n${item.prompt.substring(0, 100)}...`}
                            >
                              {item.title}
                            </button>
                          ))
                      ) : (
                        <Text type="secondary" className="text-xs">
                          {loadingInspirations ? '加载中...' : '暂无灵感，请在管理后台添加'}
                        </Text>
                      )}
                      </div>
                    </div>
                  </div>

                  {/* 主输入区域卡片 */}
                  <div className="relative mb-4 shadow-md hover:shadow-lg transition-all duration-300" style={{
                    padding: '2px',
                    borderRadius: 'var(--radius-xl)',
                    // 边框渐变：从右到左
                    background: 'linear-gradient(to left, rgba(59, 130, 246, 0.3), var(--secondary-color))'
                  }}>
                    <div className="bg-gray-50 w-full h-full rounded-xl" style={{
                      padding: 'var(--spacing-md)',
                      borderRadius: 'calc(var(--radius-xl) - 2px)'
                    }}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">✍️</span>
                          <Text className="text-sm font-bold text-gray-700">视频描述</Text>
                        </div>
                        <Button 
                          type="link" 
                          size="small" 
                          icon={<BulbOutlined />}
                          loading={polishing}
                          onClick={async () => {
                            const currentPrompt = form.getFieldValue('prompt');
                            if (!currentPrompt || !currentPrompt.trim()) {
                              message.warning('请先输入视频描述内容');
                              return;
                            }
                            
                            try {
                              setPolishing(true);
                              const response = await textService.polishPrompt(currentPrompt, 'video');
                              if (response.success) {
                                form.setFieldsValue({ prompt: response.data.polishedText });
                                setPromptValue(response.data.polishedText);
                                
                                const cost = response.data?.cost || 0;
                                message.success(cost > 0 ? `润色成功，消耗 ${cost} 积分` : '润色成功');
                                
                                // 🔥 修复：润色成功后立即刷新用户积分（延迟一下确保后端事件已发送）
                                const { refreshUser } = useAuthStore.getState();
                                // 立即刷新一次
                                try {
                                  await refreshUser();
                                  console.log('✅ [VideoPage] 润色成功，用户积分已刷新');
                                } catch (refreshError) {
                                  console.warn('⚠️ [VideoPage] 刷新用户积分失败:', refreshError);
                                }
                                // 延迟再刷新一次，确保后端事件处理完成
                                setTimeout(async () => {
                                  try {
                                    await refreshUser();
                                    console.log('✅ [VideoPage] 延迟刷新用户积分完成');
                                  } catch (refreshError) {
                                    console.warn('⚠️ [VideoPage] 延迟刷新用户积分失败:', refreshError);
                                  }
                                }, 500);
                              }
                            } catch (error: any) {
                              message.error(error.response?.data?.message || '润色失败');
                            } finally {
                              setPolishing(false);
                            }
                          }}
                          className="text-blue-500 hover:text-blue-600"
                        >
                          {polishing ? '文案润色中...' : '文案润色'}
                        </Button>
                      </div>
                      <Form.Item
                        name="prompt"
                        rules={[{ required: true, message: '请输入视频描述' }]}
                        className="mb-0"
                      >
                        <div className="relative">
                        <TextArea
                          ref={promptInputRef}
                          rows={8}
                          placeholder="输入视频要求或文案润色...（比如：一只可爱的小猫在花园里玩耍，阳光明媚，4K高清画质）"
                          className="border-0 shadow-none resize-none focus:shadow-none bg-white text-gray-700 placeholder-gray-400 rounded-xl video-prompt-textarea"
                          style={{ 
                            resize: 'none',
                            boxShadow: 'none',
                            border: 'none',
                            outline: 'none',
                            paddingBottom: '16px',
                            paddingRight: '80px',  // 右侧留出空间给生成按钮
                            fontSize: '15px',
                            background: 'white',
                            lineHeight: '1.8',
                            paddingTop: '16px',
                            paddingLeft: '104px'  // 桌面端：104px（64px 图标 + 间距），移动端由CSS控制
                          }}
                          value={promptValue}
                          onChange={(e) => {
                            setPromptValue(e.target.value)
                            try { form.setFieldsValue({ prompt: e.target.value }) } catch {}
                          }}
                          maxLength={2000}
                          showCount={false}
                        />
                        
                        {/* 移动端右下角 清空文案按钮 */}
                        {isMobile && promptValue && promptValue.trim() && (
                          <Button
                            type="text"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setPromptValue('');
                              try {
                                form.setFieldsValue({ prompt: '' });
                              } catch (err) {
                                console.warn('清空 form prompt 字段失败:', err);
                              }
                              if (promptInputRef.current) {
                                promptInputRef.current.focus();
                              }
                            }}
                            className="absolute z-20 text-gray-400 hover:text-blue-600 transition-colors"
                            style={{
                              bottom: '4px',
                              right: '4px',
                              height: 'auto',
                              padding: '4px',
                              minWidth: 'auto',
                              background: 'transparent',
                              border: 'none',
                              boxShadow: 'none',
                            }}
                            aria-label="清空文案"
                          >
                            <ClearOutlined style={{ fontSize: '15px' }} />
                          </Button>
                        )}
                        
                        {/* 输入框内左上角 - 上传/预览区域 */}
                        <div className="absolute top-2 left-2 z-10 upload-reference-image">
                          {/* 改为上下排布参考图与上传入口 */}
                          <div className="flex flex-col gap-1.5">
                            {/* 已上传参考图缩略图（最多3张） */}
                            {refImages.map((item, index) => (
                              <div
                                key={index}
                                className="w-16 h-16 rounded-md overflow-hidden relative group bg-transparent"
                              >
                                <img 
                                  src={item.previewUrl} 
                                  alt={`参考图${index + 1}`} 
                                  className="w-full h-full object-cover cursor-pointer"
                                onClick={() => {
                                  modal.info({
                                      title: null,
                                      centered: true,
                                    icon: null,
                                      content: (
                                        <div className="relative inline-block bg-black/20 border border-white/30 rounded-md" style={{ width: 'fit-content' }}>
                                          <Button
                                            type="text"
                                            size="small"
                                            onClick={() => Modal.destroyAll()}
                                            className="!m-0 !p-0 absolute top-2 right-2 w-8 h-8 rounded-full bg-black/60 hover:bg-black/70 text-white flex items-center justify-center"
                                            icon={<CloseOutlined style={{ fontSize: 16 }} />}
                                          />
                                          <img
                                            src={item.previewUrl}
                                            alt="参考图预览"
                                            className="object-contain rounded-md"
                                            style={{
                                              background: 'transparent',
                                              display: 'block',
                                              maxWidth: '95vw',
                                              maxHeight: '90vh'
                                            }}
                                          />
                                        </div>
                                      ),
                                      width: undefined,
                                    footer: null,
                                      styles: {
                                        body: { padding: 0, background: 'transparent' },
                                        content: { padding: 0, background: 'transparent' }
                                      },
                                      bodyStyle: { padding: 0 },
                                      maskClosable: true
                                  })
                                }} 
                              />
                                {/* 删除按钮（PC 端样式 + 移动端通过 CSS 缩小，参考 PC 效果） */}
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<CloseOutlined />}
                                  className="ref-image-close-btn absolute -top-1 -right-1 w-4 h-4 min-w-4 bg-gray-200 text-gray-700 hover:bg-gray-300 rounded-full flex items-center justify-center opacity-40 group-hover:opacity-100 transition-opacity duration-200 shadow-lg text-[10px] leading-none"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleRemoveImage(index)
                                  }}
                                />
                                {/* 模式标记：首帧 / 尾帧 / 参考图 */}
                                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] text-center px-1 py-0.5">
                                  {refImages.length === 2 && index === 0 && '首帧'}
                                  {refImages.length === 2 && index === 1 && '尾帧'}
                                  {refImages.length !== 2 && `参考图${index + 1}`}
                                </div>
                              </div>
                            ))}

                            {/* 上传入口：最多 3 张 */}
                            {refImages.length < 3 && (
                              <Upload
                                className="!m-0 !p-0 w-16 h-16"
                                style={{ width: '4rem', height: '4rem', display: 'block' }}
                                accept="image/jpeg,image/png,image/webp,image/gif"
                                showUploadList={false}
                                beforeUpload={async (file) => {
                                  // 文件格式检查
                                  const isImage = file.type.startsWith('image/')
                                  if (!isImage) {
                                    message.error('只能上传图片文件（JPG/PNG/WEBP/GIF）')
                                    return false
                                  }
                                  
                                  // 文件大小检查（5MB）
                                  const isLt5M = file.size / 1024 / 1024 < 5
                                  if (!isLt5M) {
                                    message.error('图片大小不能超过 5MB')
                                    return false
                                  }

                                  try {
                                    setUploadingImage(true)
                                    message.loading('正在上传参考图...', 0)
                                    
                                    const formData = new FormData()
                                    formData.append('file', file)
                                    
                                    const response = await apiClient.post('/video/upload-image', formData, {
                                      headers: {
                                        'Content-Type': 'multipart/form-data'
                                      },
                                      timeout: 120000
                                    })
                                    
                                    message.destroy()
                                    
                                    if (response.success) {
                                      const previewUrl = URL.createObjectURL(file)
                                      setRefImages(prev => {
                                        if (prev.length >= 3) return prev
                                        return [...prev, { file, url: response.data.url, previewUrl }]
                                      })
                                      message.success('参考图上传成功！')
                                      console.log('✅ [参考图上传成功]:', response.data.url)
                                    } else {
                                      message.error(response.message || '上传失败')
                                    }
                                  } catch (error: any) {
                                    message.destroy()
                                    console.error('上传失败:', error)
                                    message.error('上传失败: ' + (error?.response?.data?.message || error?.message || '网络错误'))
                                  } finally {
                                    setUploadingImage(false)
                                  }
                                  
                                  return false
                                }}
                              >
                                <div className="w-full h-full bg-transparent hover:bg-blue-50/30 cursor-pointer transition-all duration-200 flex items-center justify-center !m-0 !p-0 rounded-md border border-dashed border-gray-300">
                                    {uploadingImage ? (
                                    <div className="flex flex-col items-center justify-center text-blue-500" style={{ gap: '2px' }}>
                                      <div className="animate-spin leading-none" style={{ fontSize: 'var(--font-h4-mobile)' }}>⏳</div>
                                      <div className="text-[11px] leading-none">上传中</div>
                                    </div>
                                  ) : (
                                    <div className="flex flex-col items-center justify-center" style={{ gap: '2px' }}>
                                      <div className="text-gray-600 group-hover:text-blue-500 transition-colors font-semibold leading-none" style={{ fontSize: 'var(--font-h1-mobile)' }}>
                                        +
                                      </div>
                                      <div className="text-[11px] text-gray-400 leading-none whitespace-nowrap">
                                        参考图
                                      </div>
                                    </div>
                                    )}
                                  </div>
                              </Upload>
                            )}
                          </div>

                          {/* 模式提示文案（精简文案，避免在移动端换行过多） */}
                          <div className="mt-1 text-[10px] text-gray-500 leading-snug">
                            {refImages.length === 0 && '参考图：最多 3 张（Veo3 可用）'}
                            {refImages.length === 1 && '1 张参考图（可再传 1 张开启动画首尾帧）'}
                            {refImages.length === 2 && '首尾帧（1=首帧，2=尾帧）'}
                            {refImages.length >= 3 && '多图参考（3 张 urls，建议 16:9）'}
                          </div>
                        </div>
                      
                        {/* 内联错误提示 */}
                        {inlineError && (
                          <div className="mt-1 text-xs text-red-500 leading-snug">{inlineError}</div>
                        )}
                        
                        {/* 桌面端生成按钮 - 在输入框内的右下角 */}
                        {!isMobile && (
                          <div className="absolute bottom-2 right-2 w-auto z-30">
                          {(() => {
                            const { user } = useAuthStore.getState();
                            const cost = calculateVideoCost();
                            const isPointsInsufficient = !user || (user.pointsBalance < cost && user.credits < cost);
                            
                            return (
                              <>
                                {isPointsInsufficient && (
                                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                    <div className="flex items-center gap-2 text-yellow-800">
                                      <span>⚠️</span>
                                      <span className="text-sm font-medium">积分不足</span>
                        </div>
                                    <div className="text-xs text-yellow-700 mt-1">
                                      生成需要 {cost} 积分，当前余额: {user?.pointsBalance || 0} 积分
                        </div>
                            <Button 
                                      size="small" 
                                      type="primary" 
                                      onClick={() => navigate('/payment')}
                                      className="mt-2 w-auto"
                                    >
                                      立即充值
                            </Button>
                                  </div>
                                )}
                                
                            <Button 
                                  type="primary"
                                  htmlType="submit"
                                  loading={loading}
                                  disabled={loading || uploadingImage || isPointsInsufficient}
                                  className={`h-12 px-8 py-3 rounded-xl font-semibold text-base shadow-lg transition-all duration-300 ${
                                    (uploadingImage || isPointsInsufficient) 
                                      ? 'bg-gray-300 cursor-not-allowed border-none' 
                                      : 'bg-gradient-to-r from-blue-500 via-purple-500 to-purple-600 hover:from-blue-600 hover:via-purple-600 hover:to-purple-700 hover:shadow-xl hover:scale-105 active:scale-100 border-none generate-button-desktop'
                                  }`}
                                  style={{
                                    background: (uploadingImage || isPointsInsufficient) 
                                      ? '#d1d5db' 
                                      : 'linear-gradient(to right, #3b82f6, #a855f7, #9333ea)',
                                    backgroundImage: (uploadingImage || isPointsInsufficient) 
                                      ? 'none' 
                                      : undefined,
                                    boxShadow: (uploadingImage || isPointsInsufficient)
                                      ? 'none'
                                      : '0 10px 25px -5px rgba(139, 92, 246, 0.3), 0 8px 10px -6px rgba(139, 92, 246, 0.2)',
                                    border: 'none'
                                  }}
                                >
                                  {loading && buttonTextState === 'generating' ? (
                                    <span className="flex items-center gap-2 text-white" style={{ fontWeight: 700 }}>
                                      <span className="animate-spin">⏳</span>
                                      生成中...
                                    </span>
                                  ) : loading && buttonTextState === 'waiting' ? (
                                    <span className="flex items-center gap-2 text-white" style={{ fontWeight: 700 }}>
                                      <HistoryOutlined className="text-lg" />
                                    预估1-5分钟生成 历史中查看
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-2 text-white" style={{ fontWeight: 700 }}>
                                      <VideoCameraOutlined className="text-lg" />
                                      {uploadingImage ? '参考图上传中...' : (isPointsInsufficient ? '积分不足' : '生成视频')}
                                    </span>
                                  )}
                            </Button>
                              </>
                            );
                          })()}
                          </div>
                        )}
                        </div>
                      </Form.Item>
                    </div>
                  </div>

                  {/* 角色快捷区域 + 参数设置卡片 */}
                  <div className="unified-card mb-4">

                    {/* 角色列表 - 单独一行 */}
                    <div className="mb-4 pb-3 border-b border-gray-100">
                      <div className="flex items-center gap-2 mb-2 md:mb-3">
                        <UserOutlined className="text-gray-600" style={{ fontSize: '14px' }} />
                        <span 
                             onClick={handleOpenCharacterModal}
                          className="text-sm text-gray-700 font-semibold cursor-pointer hover:text-gray-900 transition-colors"
                           >
                          选择角色
                        </span>
                        </div>
                      {/* 角色头像列表 - 公共角色在前，用户角色在后，末尾添加上传按钮 */}
                      {/* 调整 gap 从 2 -> 1，缩小左右间距，让角色头像更紧凑一些 */}
                      <div className="flex items-start gap-1 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 min-h-[70px]">
                          {(() => {
                            // 将角色分为公共角色和用户角色，公共角色在前
                            const globalChars = availableCharacters.filter(c => c.isGlobal);
                            const userChars = availableCharacters.filter(c => !c.isGlobal);
                            const sortedChars = [...globalChars, ...userChars];
                            
                            return (
                              <>
                                {sortedChars.map((char) => {
                                  const isSelected = characters.some(
                                    (c) => c.url === char.videoUrl && c.timestamps === char.timestamps
                                  )
                                  return (
                                    <div
                                      key={char.id}
                                      className="flex flex-col items-center gap-1 flex-shrink-0"
                                      style={{ width: '60px', minWidth: '60px', maxWidth: '60px' }}
                                      onClick={() => {
                                        let newCharacters: Array<{ url: string; timestamps: string }>
                                        if (isSelected) {
                                          // 取消选择
                                          newCharacters = characters.filter(
                                            (c) => !(c.url === char.videoUrl && c.timestamps === char.timestamps)
                                          )
                                        } else {
                                          // 选择角色
                                          newCharacters = [
                                            ...characters,
                                            { url: char.videoUrl, timestamps: char.timestamps }
                                          ]
                                        }
                                        setCharacters(newCharacters)
                                        updatePromptWithCharacters(newCharacters)
                                      }}
                                    >
                                      <Tooltip title={char.name}>
                                        <div
                                          className={`
                                            relative w-12 h-12 rounded-full overflow-hidden cursor-pointer
                                            border-2 transition-all duration-200
                                            ${isSelected 
                                              ? 'border-blue-500 ring-2 ring-blue-200 shadow-md' 
                                              : 'border-gray-200 hover:border-blue-300'
                                            }
                                            hover:scale-110 active:scale-95
                                          `}
                                        >
                                          {/* 视频缩略图或默认图标 */}
                                          {char.videoUrl && (char.videoUrl.startsWith('http://') || char.videoUrl.startsWith('https://')) ? (
                                            <div className="relative w-full h-full">
                                              <video
                                                src={char.videoUrl}
                                                className="w-full h-full object-cover"
                                                muted
                                                playsInline
                                                preload="metadata"
                                                onLoadedMetadata={(e) => {
                                                  // 跳转到时间戳的开始位置
                                                  const video = e.target as HTMLVideoElement
                                                  if (video && char.timestamps) {
                                                    const startTime = parseFloat(char.timestamps.split(',')[0]) || 0
                                                    video.currentTime = Math.min(startTime, video.duration || 0)
                                                  }
                                                }}
                                                onError={(e) => {
                                                  // 视频加载失败时显示默认图标
                                                  const video = e.currentTarget as HTMLVideoElement
                                                  console.warn('视频缩略图加载失败:', char.videoUrl)
                                                  // 阻止重复请求：清空 src
                                                  video.src = ''
                                                  video.load() // 清空加载队列
                                                }}
                                              />
                                              {/* 播放图标覆盖层 */}
                                              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 opacity-0 hover:opacity-100 transition-opacity">
                                                <PlayCircleOutlined className="text-white text-xl" />
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                                              <VideoCameraOutlined className="text-white text-lg" />
                                            </div>
                                          )}
                                          {/* 选中标记 */}
                                          {isSelected && (
                                            <div className="absolute top-0 right-0 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center border-2 border-white">
                                              <CheckCircleOutlined className="text-white text-xs" />
                                            </div>
                                          )}
                                          {/* 公共角色标记 */}
                                          {char.isGlobal && (
                                            <div className="absolute bottom-0 left-0 w-3 h-3 bg-green-500 rounded-full border border-white"></div>
                                          )}
                                        </div>
                                      </Tooltip>
                                      {/* 角色名称 */}
                                      <span 
                                        className="text-xs text-gray-600 text-center truncate" 
                                        title={char.name}
                                        style={{ width: '60px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                      >
                                        {char.name}
                                      </span>
                                    </div>
                                  )
                                })}
                                {/* 上传角色按钮 */}
                                <div
                                  className="flex flex-col items-center gap-1 flex-shrink-0"
                                  style={{ width: '60px', minWidth: '60px', maxWidth: '60px' }}
                                  onClick={handleOpenCharacterModal}
                                >
                                  <Tooltip title="上传新角色">
                                    <div className="relative w-12 h-12 rounded-full overflow-hidden cursor-pointer border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 flex items-center justify-center hover:scale-110 active:scale-95">
                                      <PlusOutlined className="text-2xl text-gray-400 hover:text-blue-500 transition-colors" />
                                    </div>
                                  </Tooltip>
                                  <span 
                                    className="text-xs text-gray-600 text-center truncate"
                                    style={{ width: '60px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  >
                                    上传角色
                                  </span>
                                </div>
                              </>
                            )
                          })()}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mb-2 md:mb-3">
                      <SettingOutlined className="text-gray-600" style={{ fontSize: '14px' }} />
                      <span className="text-sm text-gray-700 font-semibold">生成参数</span>
                        </div>
                        <div className="flex items-center gap-3 md:gap-4 flex-nowrap overflow-x-auto">
                          {/* 视频比例选择 */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <span className="text-sm text-gray-600 font-semibold">比例</span>
                        <Button
                              type="default"
                              size="small"
                              className="h-8 px-3 rounded-xl text-sm bg-white text-gray-700 border-2 border-gray-200 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 hover:border-blue-200 whitespace-nowrap shadow-lg transition-all duration-300 hover:scale-105"
                              onClick={() => {
                                // 切换比例
                                setOrientation(orientation === 'PORTRAIT' ? 'LANDSCAPE' : 'PORTRAIT');
                              }}
                            >
                              {orientation === 'PORTRAIT' ? '9:16' : '16:9'}
                        </Button>
                      </div>

                          {/* 视频时长 */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <span className="text-sm text-gray-600 font-semibold">时长</span>
                            <Select
                              value={duration}
                              onChange={(value) => setDuration(value)}
                              size="small"
                              className="min-w-[100px] max-w-[120px]"
                              style={{ height: '32px' }}
                              placement={isMobile ? "topLeft" : "bottomLeft"}
                              getPopupContainer={() => document.body}
                            >
                              <Select.Option value={10}>10秒</Select.Option>
                              <Select.Option value={15}>15秒</Select.Option>
                              <Select.Option value={30}>30秒</Select.Option>
                            </Select>
                          </div>


                          {/* AI模型选择 */}
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <span className="text-sm text-gray-600 font-semibold whitespace-nowrap">模型</span>
                            <Select
                              value={selectedModel}
                              onChange={setSelectedModel}
                              size="small"
                              className="min-w-[160px] max-w-[200px] model-selector"
                              style={{ height: '32px' }}
                              loading={loadingProviders || loadingModels}
                              placeholder={videoModels.length === 0 ? "请管理员配置API服务商" : "选择模型"}
                              disabled={videoModels.length === 0}
                              placement={isMobile ? "topLeft" : "topLeft"}
                              getPopupContainer={() => document.body}
                              optionLabelProp="label"
                              styles={{ 
                                popup: { root: { minWidth: 300, maxWidth: 400 } }
                              }}
                            >
                              {videoModels.length === 0 ? (
                                <Select.Option value="" disabled label="">
                                  <div className="flex items-center gap-2 text-gray-400">
                                    <InfoCircleOutlined />
                                    <span className="text-sm">请管理员在控制台配置API服务商</span>
                        </div>
                                </Select.Option>
                              ) : (
                                videoModels.map((model) => {
                                  // 根据当前生成类型动态计算积分消耗
                                  let currentCost = model.cost;
                                  if (model.creditCosts) {
                                    if (generationType === 'TEXT_TO_VIDEO') {
                                      currentCost = model.creditCosts.textToVideo || model.cost;
                                    } else if (generationType === 'IMAGE_TO_VIDEO') {
                                      currentCost = model.creditCosts.imageToVideo || model.cost;
                                    } else if (generationType === 'VIDEO_TO_VIDEO') {
                                      currentCost = model.creditCosts.videoToVideo || model.creditCosts.imageToVideo || model.cost;
                                    }
                                  }
                                  
                                  return (
                                    <Select.Option key={model.id} value={model.id} label={model.name}>
                                      <div className="flex items-center justify-between w-full py-1">
                                        <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
                                          <span className="font-medium text-gray-800 text-sm whitespace-nowrap">{model.name}</span>
                                          <span className="text-xs text-gray-500 whitespace-nowrap">({model.providerName})</span>
                                        </div>
                                        <span className="text-xs text-blue-500 font-semibold ml-2 flex-shrink-0">{currentCost}积分</span>
                                      </div>
                                    </Select.Option>
                                  );
                                })
                              )}
                            </Select>
                          </div>
                        </div>
                  </div>

                </Form>
              </motion.div>
                       </div>

            {/* 右侧：生成预览 - 占1列 (1/3) */}
            <div className="lg:col-span-1 hidden md:block">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sticky top-6 flex flex-col"
              >
                  <div className="flex items-center justify-between mb-3 py-0 min-h-[40px]">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">🎬</span>
                      <Title level={4} className="!mb-0 font-bold">生成预览</Title>
                    </div>
                    <Space>
                      <Button 
                        icon={<HistoryOutlined />}
                        onClick={() => navigate('/history')}
                        size="small"
                        className="rounded-lg shadow-sm hover:shadow-md transition-all"
                      >
                        <span className="hidden md:inline">历史</span>
                        <span className="md:hidden">历史记录</span>
                      </Button>
                    </Space>
                  </div>

                <div className="flex-1 flex flex-col -mt-2">
                          <AnimatePresence mode="wait">
                             {!currentGeneration ? (
                               <motion.div 
                                 key="empty" 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="text-center py-16 px-8 flex-1 flex flex-col justify-center"
                      >
                          <motion.div
                            animate={{ 
                            y: [0, -10, 0],
                            scale: [1, 1.05, 1],
                            }}
                            transition={{ 
                            duration: 3,
                              repeat: Infinity,
                              repeatType: "reverse"
                            }}
                          className="inline-block text-8xl"
                          >
                          🎬✨
                          </motion.div>
                        <Title level={3} className="text-gray-600 mb-4 font-bold">
                          开启你的创作之旅
                        </Title>
                        <Text type="secondary" className="text-lg mb-6 max-w-md">
                          输入你的创意灵感，让AI为你生成精彩的视频内容
                        </Text>
                        <div className="flex items-center gap-2 justify-center flex-nowrap">
                          <span className="px-2 py-1 bg-blue-100 text-blue-600 rounded-full text-xs whitespace-nowrap">🎨创意无限</span>
                          <span className="px-2 py-1 bg-purple-100 text-purple-600 rounded-full text-xs whitespace-nowrap">⚡快速生成</span>
                          <span className="px-2 py-1 bg-pink-100 text-pink-600 rounded-full text-xs whitespace-nowrap">🎯精准控制</span>
                        </div>
                               </motion.div>
                             ) : (
                      <motion.div
                        key="results"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="space-y-4 flex-1 flex flex-col"
                      >
                        {/* 视频展示区域 */}
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8, y: 20 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          transition={{ duration: 0.4 }}
                          className="group relative"
                          style={{ marginTop: '24px' }}
                        >
                          {(() => {
                            console.log('🎭 [渲染] 检查状态:', {
                              status: currentGeneration.status,
                              isFailed: currentGeneration.status === 'failed',
                              errorMessage: currentGeneration.errorMessage
                            })
                            return currentGeneration.status === 'failed'
                          })() ? (
                            // ❌ 失败提示卡片（立即停止预览动画）
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl min-h-[220px] bg-red-50 border border-red-200 flex items-center justify-center p-6 text-center">
                              <div>
                                <div className="text-4xl mb-3">⚠️</div>
                                <div className="text-red-600 font-semibold mb-2">生成失败</div>
                                <div className="text-red-500 text-sm mb-4">
                                  {currentGeneration.errorMessage || '生成失败，请更换参考图或稍后重试'}
                                      </div>
                                <div className="flex items-center justify-center gap-2">
                                  <Button 
                                    onClick={() => {
                                      // 🔥 修复：在重新生成前，先清空失败状态，避免状态冲突
                                      console.log('🔄 [重新生成] 用户点击重新生成按钮，清空失败状态')
                                      setCurrentGeneration(null)
                                      setInlineError(null)
                                      // 延迟一下再提交，确保状态已清空
                                      setTimeout(() => {
                                        form.submit()
                                      }, 100)
                                    }} 
                                    type="primary" 
                                    size="small"
                                  >
                                    重新生成
                                  </Button>
                                  <Button onClick={() => navigate('/history')} size="small">前往历史</Button>
                                    </div>
                               </div>
                       </div>
                          ) : (currentGeneration.status === 'GENERATING' || currentGeneration.status === 'PENDING' || currentGeneration.status === 'PROCESSING' || !currentGeneration.outputVideo) ? (
                            // 🎬 生成中预览卡片 - 美化版
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl min-h-[280px]">
                              {/* 动态渐变背景 */}
                              <div className="absolute inset-0 bg-gradient-to-br from-purple-400 via-pink-400 to-blue-400 opacity-80 animate-pulse"></div>
                              <div className="absolute inset-0 bg-gradient-to-tr from-yellow-300/30 via-transparent to-blue-300/30 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                              
                              {/* 星光粒子效果 */}
                              <div className="absolute inset-0 overflow-hidden">
                                <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white rounded-full animate-ping opacity-75"></div>
                                <div className="absolute top-3/4 right-1/4 w-1 h-1 bg-yellow-200 rounded-full animate-ping opacity-60" style={{ animationDelay: '0.3s' }}></div>
                                <div className="absolute bottom-1/3 left-1/3 w-1.5 h-1.5 bg-pink-200 rounded-full animate-ping opacity-70" style={{ animationDelay: '0.6s' }}></div>
                    </div>
                              
                              {/* 内容区 */}
                              <div className="relative backdrop-blur-sm bg-white/20 flex flex-col items-center justify-center p-8 text-center h-full min-h-[280px]">
                                {/* 电影场记板图标 + 旋转动画 */}
                                <div className="relative mb-5">
                                  <div className="w-14 h-14 bg-gradient-to-br from-white to-gray-100 rounded-2xl shadow-lg flex items-center justify-center transform hover:scale-110 transition-transform duration-300">
                                    <span className="text-3xl animate-bounce">🎬</span>
                 </div>
                                  {/* 环形进度 */}
                                  <div className="absolute -inset-2 border-3 border-white/40 border-t-white rounded-full animate-spin"></div>
              </div>

                                {/* 主标题 - 大字强调 */}
                                <div className="text-xl font-bold text-white mb-2 drop-shadow-lg">
                                  ✨ 正在为你编织画面
            </div>
                                
                                {/* 副标题 - 沉浸式文案 */}
                                <div className="text-sm text-white/95 mb-3 font-medium drop-shadow">
                                  🎬 AI 正在导演你的专属短片
        </div>

                                {/* 等待时间 - 轻松化 */}
                                <div className="text-xs text-white/85 mb-3 flex items-center gap-1.5">
                                  <span className="animate-pulse text-base">⏳</span>
                                  <span>片刻等待，即将呈现</span>
                 </div>
                                
                                {/* 底部提示 - 简化突出 */}
                                <div className="mt-2 px-3 py-1.5 bg-white/95 rounded-full text-xs text-indigo-600 font-medium shadow-lg backdrop-blur">
                                  💡 预览将自动保存至历史记录
                                </div>
                              </div>
                            </div>
                          ) : (
                            // 完成的结果卡片
                            <div className="relative rounded-lg overflow-hidden bg-white shadow-md">
                              <video 
                                controls 
                                className="w-full h-auto block"
                                style={{ 
                                  objectFit: 'cover',
                                  display: 'block',
                                  backgroundColor: 'transparent'
                                }}
                                autoPlay
                                crossOrigin="anonymous"
                                poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 450'%3E%3Crect fill='%23ffffff' width='800' height='450'/%3E%3C/svg%3E"
                              >
                                <source src={currentGeneration.outputVideo} type="video/mp4" />
                              </video>
                            </div>
                          )}
                        </motion.div>

                        {/* 底部操作按钮（与图片页一致：始终显示 下载/重新生成；下载在无结果时禁用） */}
                        <div className="flex justify-center space-x-3 mt-4">
                 <Button
                   type="primary"
                            icon={<DownloadOutlined />}
                            disabled={!currentGeneration.outputVideo}
                            onClick={async () => {
                              try {
                                // 移动端特殊处理
                                if (isMobileDevice()) {
                                  // 移动端视频：打开新窗口，提示用户长按保存
                                  window.open(currentGeneration.outputVideo, '_blank')
                                  if (isIOSDevice()) {
                                    message.info('视频已打开，请长按视频选择"保存到相册"', 5)
                                  } else {
                                    message.info('视频已打开，请点击右上角菜单选择"下载"或"保存"', 5)
                                  }
                                  return
                                }
                                
                                message.loading('正在下载视频...', 0)
                                
                                try {
                                  // 尝试使用 fetch 下载（如果CORS允许）
                                  const response = await fetch(currentGeneration.outputVideo, {
                                    mode: 'cors'
                                  })
                                const blob = await response.blob()
                                
                                // 创建下载链接
                                const url = window.URL.createObjectURL(blob)
                                const link = document.createElement('a')
                                link.href = url
                                link.download = `video-${Date.now()}.mp4`
                                link.style.display = 'none'
                                
                                // 添加到 DOM，点击，然后移除
                                document.body.appendChild(link)
                                link.click()
                                document.body.removeChild(link)
                                
                                // 释放 URL 对象
                                window.URL.revokeObjectURL(url)
                                
                                message.destroy()
                                message.success('下载成功')
                                } catch (fetchError: any) {
                                  // 如果 fetch 失败（可能是CORS问题），使用直接链接方式
                                  console.warn('Fetch下载失败，使用直接链接方式:', fetchError)
                                  const link = document.createElement('a')
                                  link.href = currentGeneration.outputVideo
                                  link.download = `video-${Date.now()}.mp4`
                                  link.target = '_blank'
                                  link.rel = 'noopener noreferrer'
                                  link.style.display = 'none'
                                  document.body.appendChild(link)
                                  link.click()
                                  document.body.removeChild(link)
                                  
                                  message.destroy()
                                  message.success('已打开下载链接')
                                }
                              } catch (error) {
                                message.destroy()
                                message.error('下载失败，请重试')
                                console.error('下载失败:', error)
                              }
                            }}
                            className="rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                          >
                            下载视频
                 </Button>
               <Button 
                            icon={<ReloadOutlined />}
                            onClick={() => {
                              // 🔥 修复：在重新生成前，先清空失败状态，避免状态冲突
                              if (currentGeneration && currentGeneration.status?.toLowerCase() === 'failed') {
                                console.log('🔄 [重新生成] 用户点击重新生成按钮，清空失败状态')
                                setCurrentGeneration(null)
                                setInlineError(null)
                                // 延迟一下再提交，确保状态已清空
                                setTimeout(() => {
                                  form.submit()
                                }, 100)
                              } else {
                                form.submit()
                              }
                            }}
                            className="rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                          >
                            重新生成
                          </Button>
             </div>
                      </motion.div>
                    )}
                </AnimatePresence>
                </div>

                  {/* 创作提示卡片 - 独立模块 */}
                  <div className="mt-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border-2 border-amber-200 shadow-md hover:shadow-lg transition-all duration-300">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-2xl">💡</span>
                      <Text className="text-sm font-bold text-amber-800">创作提示</Text>
                    </div>
                    <ul className="text-sm text-amber-700 space-y-2.5 mb-0 leading-relaxed">
                      <li className="flex items-start gap-2">
                        <span className="text-amber-500 mt-0.5">✓</span>
                        <span>描述要具体、生动，包含场景、光线等细节</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-500 mt-0.5">✓</span>
                        <span>避免使用版权人物、品牌名称</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-500 mt-0.5">✓</span>
                        <span>添加艺术风格可获得更好效果（如：电影感）</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-amber-500 mt-0.5">✓</span>
                        <span>简单场景生成速度更快</span>
                      </li>
                    </ul>
                  </div>
              </motion.div>
              </div>
            </div>
          </div>

            {/* 底部创作风格区域 - 移动端隐藏 */}
            <div 
              className="w-full hidden md:block container-max"
              style={{
                marginTop: 'var(--spacing-xxl)',
                marginBottom: 'var(--spacing-xxl)',
                paddingTop: 'var(--spacing-xxl)',
                paddingBottom: 'var(--spacing-xxl)'
              }}
            >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <div className="text-center" style={{ marginBottom: 'var(--spacing-xl)' }}>
              <Title level={2} className="mb-3">
                <span className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 bg-clip-text text-transparent">
                  ✨ 热门创作风格
                </span>
              </Title>
              <Text type="secondary" className="text-lg text-gray-600">💡 点击卡片快速开始创作，激发你的灵感</Text>
            </div>
            
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
              {[
                { 
                  style: '自然风光',
                  prompt: '日落时分的海滩，海浪轻拍沙滩，海鸥飞过，金色阳光洒在水面上，电影级慢镜头',
                  emoji: '🌅',
                  gradient: 'from-blue-50 to-cyan-50',
                  borderColor: 'border-blue-200'
                },
                {
                  style: '科幻场景',
                  prompt: '未来城市夜景，霓虹灯闪烁，飞行汽车穿梭，赛博朋克风格，雨夜反光',
                  emoji: '🌃',
                  gradient: 'from-purple-50 to-pink-50',
                  borderColor: 'border-purple-200'
                },
                {
                  style: '动物世界',
                  prompt: '一只可爱的小熊猫在竹林里玩耍，咀嚼竹叶，憨态可掬，4K高清，自然纪录片风格',
                  emoji: '🐼',
                  gradient: 'from-green-50 to-emerald-50',
                  borderColor: 'border-green-200'
                },
                {
                  style: '旅行记录',
                  prompt: '城市街头手持镜头行走拍摄，快速剪辑，转场流畅，音乐节奏感强',
                  emoji: '🎬',
                  gradient: 'from-orange-50 to-red-50',
                  borderColor: 'border-orange-200'
                }
              ].map((example, idx) => (
                <motion.div
                  key={idx}
                  whileHover={{ y: -8, scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                >
                  <div 
                    className={`h-full cursor-pointer rounded-3xl border-2 ${example.borderColor} bg-gradient-to-br ${example.gradient} shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden p-8 group`}
                    onClick={() => {
                      setMobileTab('create')
                      setPromptValue(example.prompt)
                      try { form.setFieldsValue({ prompt: example.prompt }) } catch {}
                      try { form.scrollToField('prompt', { block: 'center' }) } catch {}
                      setTimeout(() => { try { promptInputRef?.current?.focus?.() } catch {} }, 0)
                      message.success(`已应用"${example.style}"风格 ✨`)
                    }}
                    title={`点击应用：${example.prompt}`}
                  >
                    <div className="text-center">
                      <motion.div 
                        className="text-7xl mb-5"
                        whileHover={{ rotate: [0, -10, 10, -10, 0], scale: 1.1 }}
                        transition={{ duration: 0.5 }}
                      >
                        {example.emoji}
                      </motion.div>
                      <Title level={4} className="!mb-3 font-bold text-gray-800">{example.style}</Title>
                      <div className="inline-flex items-center gap-1.5 px-4 py-2 bg-white group-hover:bg-gradient-to-r group-hover:from-pink-50 group-hover:to-purple-50 rounded-full text-sm font-semibold text-gray-700 group-hover:text-pink-700 shadow-md mb-4 border border-gray-200 group-hover:border-pink-300 transition-all duration-300">
                        <span className="text-base">✨</span>
                        <span>点击应用</span>
                      </div>
                      <Paragraph 
                        ellipsis={{ rows: 3 }} 
                        className="text-sm text-gray-600 mb-0 leading-relaxed"
                      >
                      {example.prompt}
                      </Paragraph>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
            </div>
        </motion.div>
      </div>
    </div>

    {/* 角色选择弹窗 */}
        <Modal
          open={characterModalVisible}
      title="角色管理与选择"
          onCancel={() => setCharacterModalVisible(false)}
          onOk={handleConfirmCharacters}
      okText="应用到本次生成"
      cancelText="取消"
      width={640}
    >
      <div className="space-y-4">
        {/* 新增角色表单 - 移到上方 */}
        <div className="rounded-xl border border-gray-200 p-4 bg-blue-50/30">
          <div className="font-semibold text-sm mb-3 text-gray-700">新增角色</div>
        <Form
          form={newCharacterForm}
          layout="vertical"
          requiredMark={false}
          initialValues={{ timestamps: '0,3' }}
        >
          <Form.Item
            label="角色名称（固定唯一）"
            name="name"
            rules={[
              { required: true, message: '请输入角色名称' },
              { max: 32, message: '名称不超过 32 个字符' }
            ]}
          >
              <Input placeholder="例如：police_officer_A 或 黑发女主_A" />
          </Form.Item>
          <Form.Item
            label="角色视频 URL"
            name="url"
            rules={[
              { required: true, message: '请输入角色视频 URL' }
            ]}
          >
            <Input
                placeholder="上传成功后自动获取URL"
              addonAfter={
                <Upload
                  accept="video/mp4,video/*"
                  showUploadList={false}
                  beforeUpload={async (file) => {
                    try {
                      const isVideo = file.type.startsWith('video/')
                      if (!isVideo) {
                        message.error('只能上传视频文件')
                        return Upload.LIST_IGNORE
                      }

                      const isLt50M = file.size / 1024 / 1024 < 50
                      if (!isLt50M) {
                        message.error('视频大小不能超过 50MB')
                        return Upload.LIST_IGNORE
                      }

                      message.loading('正在上传角色视频...', 0)

                      const formData = new FormData()
                      formData.append('file', file)

                      const res = await apiClient.post('/video/upload-character-video', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                        timeout: 300000
                      })

                      message.destroy()

                      if (res.success && res.data?.url) {
                        newCharacterForm.setFieldsValue({ url: res.data.url })
                        message.success('角色视频上传成功')
                      } else {
                        message.error(res.message || '角色视频上传失败')
                      }
                    } catch (error: any) {
                      message.destroy()
                      console.error('❌ [前端] 角色视频上传失败:', error)
                      message.error(error?.response?.data?.message || error?.message || '角色视频上传失败')
                    }

                    return Upload.LIST_IGNORE
                  }}
                >
                  <Button size="small" type="link">
                    上传视频
                  </Button>
                </Upload>
              }
            />
          </Form.Item>
          <Form.Item
            label="时间范围（秒，最多 3 秒）"
            name="timestamps"
            rules={[
              { required: true, message: '请输入时间范围' },
              {
                pattern: /^\d+,\d+$/,
                  message: '格式应为 "开始秒数,结束秒数"，例如：0,3'
              }
            ]}
          >
            <Input placeholder="0,3" />
          </Form.Item>
          <div className="flex justify-end">
            <Button
              type="primary"
              loading={creatingCharacter}
              onClick={handleCreateCharacter}
            >
              新增角色
            </Button>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            说明：角色视频不可为真人视频，且时长不超过 3 秒。
            <br />
              • 选择角色后，系统会自动在提示词中插入角色名称（如 <code className="mx-1">@卡通美女</code>）。
          </div>
        </Form>
        </div>

        <Divider plain style={{ margin: '12px 0' }}>
          <span className="text-xs text-gray-400">我的角色</span>
        </Divider>

        {/* 我的角色列表 */}
        <div className="rounded-xl border border-gray-200 p-3 bg-gray-50">
          {availableCharacters.filter(c => !c.isGlobal).length === 0 ? (
            <div className="text-xs text-gray-500 text-center py-4">
              还没有保存的个人角色，可以在上方填写角色名称、视频URL和时间范围后点击"新增角色"。
            </div>
          ) : (
            <List
              size="small"
              dataSource={availableCharacters.filter(c => !c.isGlobal)}
              renderItem={(item) => (
                <List.Item className="cursor-pointer">
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-2 flex-1">
                      <Checkbox
                        checked={selectedCharacterIds.includes(item.id)}
                        onChange={(e) => {
                          e.stopPropagation()
                          setSelectedCharacterIds((prev) =>
                            prev.includes(item.id)
                              ? prev.filter((id) => id !== item.id)
                              : [...prev, item.id]
                          )
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span 
                        className="font-medium text-sm flex-1"
                        onClick={(e) => {
                          e.stopPropagation()
                          setSelectedCharacterIds((prev) =>
                            prev.includes(item.id)
                              ? prev.filter((id) => id !== item.id)
                              : [...prev, item.id]
                          )
                        }}
                      >
                        {item.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">
                        {item.timestamps} · {item.sourceType === 'FROM_VIDEO' ? '来源视频' : '上传视频'}
                      </span>
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={async (e) => {
                          e.stopPropagation()
                          modal.confirm({
                            title: '确认删除',
                            content: `确定要删除角色"${item.name}"吗？此操作不可恢复。`,
                            okText: '删除',
                            okType: 'danger',
                            cancelText: '取消',
                            onOk: async () => {
                              try {
                                const res = await apiClient.delete(`/video/characters/${item.id}`)
                                if (res.success) {
                                  message.success('角色已删除')
                                  // 重新加载角色列表
                                  await loadCharacters()
                                  // 如果删除的角色已被选中，从选中列表中移除
                                  setSelectedCharacterIds((prev) => prev.filter((id) => id !== item.id))
                                  // 更新当前选择的角色
                                  const updatedCharacters = characters.filter(
                                    (c) => !(c.url === item.videoUrl && c.timestamps === item.timestamps)
                                  )
                                  setCharacters(updatedCharacters)
                                  updatePromptWithCharacters(updatedCharacters)
                                } else {
                                  message.error(res.message || '删除失败')
                                }
                              } catch (error: any) {
                                console.error('删除角色失败:', error)
                                message.error('删除失败: ' + (error?.response?.data?.message || error.message))
                              }
                            }
                          })
                        }}
                        className="opacity-60 hover:opacity-100"
                      />
                    </div>
                  </div>
                </List.Item>
              )}
            />
          )}
        </div>

      </div>
    </Modal>

      {/* 移动端固定悬浮按钮 - 使用 Portal 渲染到 body */}
      {typeof window !== 'undefined' && isMobile && document.body && createPortal(
        <div className="mobile-fixed-button-container">
          {(() => {
            const { user } = useAuthStore.getState();
            const cost = calculateVideoCost();
            const isPointsInsufficient = !user || (user.pointsBalance < cost && user.credits < cost);
            
            return (
              <Button
                type="primary"
                onClick={() => {
                  // 手动触发表单提交
                  form.submit()
                }}
                block
                loading={loading}
                disabled={loading || uploadingImage || isPointsInsufficient}
                className="h-14 font-semibold shadow-lg transition-all duration-200 generate-button-mobile"
                style={{
                  borderRadius: '12px',
                  background: (uploadingImage || isPointsInsufficient)
                    ? 'linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%)'
                    : 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #9333ea 100%)',
                  border: 'none',
                  boxShadow: (uploadingImage || isPointsInsufficient)
                    ? 'none'
                    : '0 4px 16px rgba(59, 130, 246, 0.4), 0 2px 8px rgba(139, 92, 246, 0.3)',
                  color: '#ffffff',
                  fontWeight: 600,
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {loading && buttonTextState === 'generating' ? (
                  <span style={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className="animate-spin">⏳</span>
                    生成中...
                  </span>
                ) : loading && buttonTextState === 'waiting' ? (
                  <span style={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <HistoryOutlined style={{ fontSize: '18px' }} />
                    预估1-5分钟生成 历史中查看
                  </span>
                ) : (
                  <span style={{ fontSize: '16px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <VideoCameraOutlined style={{ fontSize: '18px' }} />
                    {uploadingImage ? '参考图上传中...' : (isPointsInsufficient ? '积分不足' : '生成视频')}
                  </span>
                )}
              </Button>
            );
          })()}
        </div>,
        document.body
      )}
    </>
  )
}

export default VideoGenerationPage
